import { useState } from 'react';
import type { AdminUser } from '../types';
import { Icon, Avatar, StatusBadge, TierBadge, UsageCell, fmt } from '../atoms';
import { FLAGS, ORG } from '../data';

function RowMenu({ u, onOpen, act, close }: {
  u: AdminUser;
  onOpen: (u: AdminUser) => void;
  act: (type: string, user: AdminUser) => void;
  close: () => void;
}) {
  const item = (icon: string, label: string, fn: () => void, cls?: string) => (
    <button className={cls ?? ''} onClick={e => { e.stopPropagation(); close(); fn(); }}>
      <Icon name={icon} size={15} />{label}
    </button>
  );
  return (
    <div className="menu" onClick={e => e.stopPropagation()}>
      {item('eye', 'View details', () => onOpen(u))}
      {u.status === 'pending' && item('check', 'Approve', () => act('approve', u))}
      {item('sliders', 'Adjust AI caps', () => act('caps', u))}
      {item('key', 'Reset password', () => act('reset', u))}
      <div className="sep" />
      {u.status === 'disabled'
        ? item('power', 'Re-enable account', () => act('enable', u))
        : u.status !== 'pending' && item('ban', 'Disable account', () => act('disable', u), 'bad')}
    </div>
  );
}

export function Users({ users, onOpen, act, query }: {
  users: AdminUser[];
  onOpen: (u: AdminUser) => void;
  act: (type: string, user: AdminUser) => void;
  query: string;
}) {
  const [filter, setFilter] = useState('all');
  const [menu, setMenu] = useState<string | null>(null);
  const q = (query ?? '').trim().toLowerCase();
  let rows = users.filter(u => {
    if (filter === 'flagged') return u.flags.length > 0;
    if (filter !== 'all' && u.status !== filter) return false;
    return true;
  });
  if (q) rows = rows.filter(u => (u.name + ' ' + u.email).toLowerCase().includes(q));

  const chips: [string, string][] = [['all', 'All'], ['active', 'Active'], ['pending', 'Pending'], ['disabled', 'Disabled'], ['flagged', 'Flagged']];

  return (
    <div className="tablewrap" onClick={() => setMenu(null)}>
      <div className="filters">
        {chips.map(([k, l]) => (
          <button key={k} className={'fchip' + (filter === k ? ' on' : '')} onClick={() => setFilter(k)}>
            {l}{k !== 'all' && ' · ' + users.filter(u => k === 'flagged' ? u.flags.length > 0 : u.status === k).length}
          </button>
        ))}
        <div className="grow" />
        <span className="res">{rows.length} of {users.length} shown · {ORG.totalUsers} total</span>
      </div>
      <table className="at">
        <thead>
          <tr>
            <th>User</th><th>Status</th><th>Tier</th><th>AI usage · this month</th>
            <th>Storage</th><th>Last active</th><th>Flags</th><th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(u => (
            <tr key={u.id} className={u.status === 'disabled' ? 'dis' : ''} onClick={() => onOpen(u)}>
              <td>
                <div className="ucell">
                  <Avatar size={34} hue={u.hue} initials={u.initials} dis={u.status === 'disabled'} />
                  <div className="who"><div className="n">{u.name}</div><div className="e">{u.email}</div></div>
                </div>
              </td>
              <td><StatusBadge status={u.status} /></td>
              <td><TierBadge tier={u.tier} /></td>
              <td>
                {u.status === 'pending'
                  ? <span style={{ color: 'var(--faint)', fontFamily: 'var(--label-font)', fontSize: 12 }}>— awaiting approval</span>
                  : <UsageCell user={u} />}
              </td>
              <td>
                <span className="mono" style={{ fontSize: 13 }}>{fmt.mb(u.db + u.s3)}</span>
                <div style={{ width: 100, marginTop: 5 }}>
                  <div className="stbar" style={{ height: 7 }}>
                    <span className="db" style={{ width: (u.db + u.s3 ? u.db / (u.db + u.s3) * 100 : 0) + '%' }} />
                    <span className="s3" style={{ width: (u.db + u.s3 ? u.s3 / (u.db + u.s3) * 100 : 0) + '%' }} />
                  </div>
                </div>
              </td>
              <td><span style={{ fontSize: 13, color: 'var(--soft)' }}>{u.last}</span></td>
              <td>
                {u.flags.length
                  ? <span className="flagdots">{u.flags.map(f => <span key={f} className={'fd ' + FLAGS[f].sev} title={FLAGS[f].label} />)}</span>
                  : <span style={{ color: 'var(--faint)' }}>—</span>}
              </td>
              <td className="rowact">
                <button className="dots" onClick={e => { e.stopPropagation(); setMenu(menu === u.id ? null : u.id); }}>
                  <Icon name="dots" />
                </button>
                {menu === u.id && <RowMenu u={u} onOpen={onOpen} act={act} close={() => setMenu(null)} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
